import {FormControl, InputLabel, MenuItem, Select} from '@mui/material'
import {Button, Typography} from '@viptech/react-components'
import {useMemo, useState} from 'react'
import {Icon} from 'src/common/generics/components/icon/IconBase'
import {Input} from 'src/components/input/Input'
import {FormModalBody, FormModalContent, FormModalFooter, FormModalHeader, FormModalIntro} from 'src/components/modal/FormModalBase'
import ModalCustom from 'src/components/modal/ModalCustom'
import useCreateAlarmCenterZone from 'src/hooks/api/alarm-center/useCreateAlarmCenterZone'
import useUpdateAlarmCenterZone from 'src/hooks/api/alarm-center/useUpdateAlarmCenterZone'
import useCameras from 'src/hooks/api/camera/useCameras'
import CloseIcon from 'src/images/closeBlueIcon.svg'
import {AlarmCenter, AlarmCenterZone} from 'src/services/api/endpoints/AlarmCenterEndpoint'
import {padPartition, padZoneNumber} from '../alarmCentersHelpers'

const ELEVATED_MODAL_Z_INDEX = 20

type Props = {
  isOpen: boolean
  alarmCenter: AlarmCenter
  zone?: AlarmCenterZone | null
  onClose: () => void
}

type FormState = {
  cameraId: number | ''
  zoneNumber: string
  partition: string
  name: string
}

function getInitialForm(zone?: AlarmCenterZone | null): FormState {
  return {
    cameraId: zone?.cameraId ?? '',
    zoneNumber: zone ? padZoneNumber(zone.zoneNumber) : '',
    partition: zone ? padPartition(zone.partition) : '',
    name: zone?.name ?? '',
  }
}

function AlarmCenterZoneFormModal({isOpen, alarmCenter, zone, onClose}: Props) {
  const isEditing = !!zone
  const formKey = zone ? `edit-${zone.id}` : 'create'

  if (!isOpen) return null

  return <AlarmCenterZoneForm key={formKey} alarmCenter={alarmCenter} zone={zone} isEditing={isEditing} onClose={onClose} />
}

function AlarmCenterZoneForm({
  alarmCenter,
  zone,
  isEditing,
  onClose,
}: {
  alarmCenter: AlarmCenter
  zone?: AlarmCenterZone | null
  isEditing: boolean
  onClose: () => void
}) {
  const [form, setForm] = useState<FormState>(() => getInitialForm(zone))

  const {data: camerasResponse, isLoading: isLoadingDevices} = useCameras({
    clientIds: [alarmCenter.clientId],
    enabled: true,
  })

  const devices = useMemo(() => camerasResponse?.data.data.entities ?? [], [camerasResponse])

  const {mutateAsync: createZone, isPending: isCreating} = useCreateAlarmCenterZone()
  const {mutateAsync: updateZone, isPending: isUpdating} = useUpdateAlarmCenterZone()

  const canSubmit = useMemo(() => {
    if (form.cameraId === '') return false
    if (!/^\d{3}$/.test(form.zoneNumber)) return false
    if (!/^\d{2}$/.test(form.partition)) return false
    return true
  }, [form])

  const handleSubmit = async () => {
    if (!canSubmit || form.cameraId === '') return

    if (isEditing && zone) {
      await updateZone({
        id: zone.id,
        cameraId: form.cameraId,
        zoneNumber: Number(form.zoneNumber),
        partition: form.partition,
        name: form.name || null,
      })
    } else {
      await createZone({
        alarmCenterId: alarmCenter.id,
        zoneNumber: Number(form.zoneNumber),
        cameraId: form.cameraId,
        partition: form.partition,
        name: form.name || null,
      })
    }

    onClose()
  }

  return (
    <ModalCustom isOpen closeModal={onClose} backgroundColor='white' width='min(520px, calc(100vw - 32px))' padding='0' zIndex={ELEVATED_MODAL_Z_INDEX}>
      <FormModalContent>
        <FormModalHeader>
          <Typography variant='p' weight={500} size='20px' color='#344054'>
            {isEditing ? 'Editar zona' : 'Cadastrar zona'}
          </Typography>
          <Icon src={CloseIcon} cursor='pointer' onClick={onClose} style={{width: '12px'}} />
        </FormModalHeader>

        <FormModalIntro>
          <Typography variant='span' size='12px' color='#98A2B3'>
            Informe os dados da zona vinculada a esta central de alarme.
          </Typography>
        </FormModalIntro>

        <FormModalBody $gap='16px'>
          <FormControl fullWidth size='small'>
            <InputLabel id='alarm-center-device-label'>Dispositivo</InputLabel>
            <Select
              labelId='alarm-center-device-label'
              label='Dispositivo'
              value={form.cameraId}
              disabled={isLoadingDevices || devices.length === 0}
              MenuProps={{style: {zIndex: ELEVATED_MODAL_Z_INDEX + 1}}}
              onChange={(e) => setForm((prev) => ({...prev, cameraId: Number(e.target.value)}))}>
              {devices.map((device) => (
                <MenuItem key={device.id} value={device.id}>
                  {device.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Input.Root
            controlled
            label='Número da zona'
            labelFontSize='14px'
            borderColor='#D0D5DD'
            fontSize='12px'
            maxLength={3}
            required
            value={form.zoneNumber}
            onKeyDown={(event) => {
              const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End']
              if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) return
              if (!/^\d$/.test(event.key)) event.preventDefault()
            }}
            onChange={(e) => setForm((prev) => ({...prev, zoneNumber: e.target.value.replace(/\D/g, '')}))}
          />

          <Input.Root
            controlled
            label='Partição'
            labelFontSize='14px'
            borderColor='#D0D5DD'
            fontSize='12px'
            maxLength={2}
            required
            value={form.partition}
            onKeyDown={(event) => {
              const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End']
              if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) return
              if (!/^\d$/.test(event.key)) event.preventDefault()
            }}
            onChange={(e) => setForm((prev) => ({...prev, partition: e.target.value.replace(/\D/g, '')}))}
          />

          <Input.Root
            controlled
            label='Nome da zona'
            labelFontSize='14px'
            borderColor='#D0D5DD'
            fontSize='12px'
            maxLength={120}
            value={form.name}
            onChange={(e) => setForm((prev) => ({...prev, name: e.target.value}))}
          />
        </FormModalBody>

        <FormModalFooter>
          <Button fontSize='12px' height='38px' width='120px' variant='outlined' color='#8E8E8E' onClick={onClose}>
            Cancelar
          </Button>
          <Button
            fontSize='12px'
            height='38px'
            width='120px'
            disabledTextColor='#8E8E8E'
            disabled={!canSubmit}
            isLoading={isCreating || isUpdating}
            onClick={() => !isCreating && !isUpdating && void handleSubmit()}>
            {isEditing ? 'Salvar' : 'Adicionar'}
          </Button>
        </FormModalFooter>
      </FormModalContent>
    </ModalCustom>
  )
}

export default AlarmCenterZoneFormModal
